import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Sparkles, Loader2, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { disputeIntelligenceService } from '../../services/disputeIntelligenceService';

const REFRESH_STATUS_LABELS = {
  connecting: 'Connecting',
  pulling_latest_report: 'Pulling latest report',
  comparing_changes: 'Comparing changes',
  refresh_complete: 'Refresh complete',
  no_changes: 'No new changes found',
  new_updates: 'New updates found',
  reconnect_required: 'Reconnect IdentityIQ',
  rate_limited: 'Please wait before refreshing',
};

export default function CreditIntelligencePanel({ token, t, ns = 'creditIntelligence' }) {
  const navigate = useNavigate();
  const [changes, setChanges] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [refreshMeta, setRefreshMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    if (!token) return;
    try {
      const [recRes, changeRes, metaRes] = await Promise.all([
        disputeIntelligenceService.getAiRecommendations(token),
        disputeIntelligenceService.getReportChanges(token),
        disputeIntelligenceService.getRefreshMeta(token),
      ]);
      setRecommendations(recRes.recommendations || []);
      setChanges(changeRes);
      setRefreshMeta(metaRes.meta || {});
    } catch {
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await disputeIntelligenceService.triggerReportRefresh(token);
      setRefreshMeta((m) => ({ ...m, refresh_status: res.status }));
      if (res.status === 'reconnect_required') {
        navigate('/settings/connections');
        return;
      }
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-stone-500 py-6">
        <Loader2 size={16} className="animate-spin" />
        {t(`${ns}.loading`)}
      </div>
    );
  }

  const timeline = changes?.timeline || [];
  const refreshLabel = REFRESH_STATUS_LABELS[refreshMeta?.refresh_status] || null;

  return (
    <div className="space-y-5" data-testid="credit-intelligence-panel">
      <div className="rounded-2xl border border-stone-200 dark:border-emerald-800/30 p-5 bg-white dark:bg-[#0d2018]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <div>
            <h3 className="font-bold text-stone-900 dark:text-white">{t(`${ns}.refreshTitle`)}</h3>
            <p className="text-sm text-stone-600 dark:text-emerald-100/80">{t(`${ns}.refreshSubtitle`)}</p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t(`${ns}.refreshButton`)}
          </Button>
        </div>
        {refreshLabel && (
          <p className="text-sm text-emerald-700 dark:text-emerald-300">{refreshLabel}</p>
        )}
        {changes?.has_report && (
          <p className="text-sm mt-2 text-stone-600 dark:text-emerald-100/80">
            {changes.has_changes ? t(`${ns}.updatesFound`) : t(`${ns}.noUpdates`)}
          </p>
        )}
      </div>

      {timeline.length > 0 && (
        <div className="rounded-2xl border border-stone-200 dark:border-emerald-800/30 p-5 bg-gradient-to-br from-white to-emerald-50/10 dark:from-[#0d2018] dark:to-[#0f241c]">
          <h3 className="font-bold text-stone-900 dark:text-white mb-3 flex items-center gap-2">
            <TrendingUp size={18} className="text-emerald-500" />
            {t(`${ns}.timelineTitle`)}
          </h3>
          <ul className="space-y-2">
            {timeline.slice(0, 8).map((entry, idx) => (
              <li
                key={idx}
                className="text-sm text-stone-700 dark:text-emerald-100 flex items-start gap-2"
              >
                <span className="text-emerald-500 mt-0.5">•</span>
                {entry.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="rounded-2xl border border-stone-200 dark:border-emerald-800/30 p-5 bg-white dark:bg-[#0d2018]">
          <h3 className="font-bold text-stone-900 dark:text-white mb-3 flex items-center gap-2">
            <Sparkles size={18} className="text-amber-500" />
            {t(`${ns}.aiTitle`)}
          </h3>
          <div className="space-y-3">
            {recommendations.map((rec) => (
              <div
                key={rec.key}
                className="rounded-xl border border-stone-100 dark:border-emerald-800/20 p-3 bg-stone-50/50 dark:bg-[#0a1812]/40"
              >
                <p className="font-semibold text-stone-900 dark:text-white text-sm">{rec.title}</p>
                <p className="text-sm text-stone-600 dark:text-emerald-100/80 mt-1">{rec.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!changes?.has_report && (
        <p className="text-sm text-stone-500 dark:text-emerald-100/50 flex items-center gap-2">
          <AlertCircle size={14} />
          {t(`${ns}.noReportYet`)}
        </p>
      )}
    </div>
  );
}
